import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import InspeccionClass from '../model/api/inspecciones/inspeccionClass';
import { LoaderService } from './loader.service';
import { MainService } from '../api/main.service';
import { DatabaseService } from '../database/database.service';
import ImagenInspeccionClass from '../model/api/inspecciones/imagenInspeccionClass';
import AdjuntoInspeccionClass from '../model/api/inspecciones/adjuntoInspeccionClass';
import { BehaviorSubject, Subject } from 'rxjs';
import { AlertService } from './alert.service';
import { PendingTasksService } from './pending-tasks.service';

@Injectable({
  providedIn: 'root'
})
export class InspeccionesService {

  constructor(
    private loader: LoaderService,
    private service: MainService,
    private database: DatabaseService,
    private alertService: AlertService,
    private pendingTasks: PendingTasksService
  ) {
    // Cualquier sincronización en curso (subida de la OT, de una firma, etc.) se refleja en InspeccionGuardando$.
    // La añadimos a la cola de tareas pendientes para que la aplicación no se recargue mientras esté en curso.
    this.InspeccionGuardando$.subscribe(id => {
      if (id != null && this.guardandoTaskId == null) {
        this.guardandoTaskId = this.pendingTasks.addTask('Sincronizando orden de trabajo')
      } else if (id == null && this.guardandoTaskId != null) {
        this.pendingTasks.removeTask(this.guardandoTaskId)
        this.guardandoTaskId = null
      }
    })
  }

  private guardandoTaskId: number = null
  public InspeccionActualizada$ = new Subject<InspeccionClass>()
  public InspeccionGuardando$ = new BehaviorSubject<number>(null)

  private isGroupVisible(group: FormGroup, array: FormArray): boolean {
    if (group.get('Visible')?.value == null || group.get('Visible')?.value == false) return false
    if (group.get('CodigoDepende')?.value != null && group.get('CodigoDepende')?.value != '') {
      const groupDepende = array.controls.find(p => p.get('Codigo')?.value == group.get('CodigoDepende')?.value) as FormGroup
      if (groupDepende == null) {
        return false // El grupo no se pinta porque depende de otro, pero el otro grupo no se encuentra, por tanto es imposible rellenarlo por lo que no debe
                     // contar para la validación
      }
      return this.isGroupVisible(groupDepende, array)
    }
    return true
  }


  public async guardarInspeccion(ordenTrabajo: InspeccionClass, inspeccionFormGroup: FormGroup, showMessages?: boolean, validarInspeccion?: boolean): Promise<void> {
    if (showMessages == null) showMessages = true;
    if (validarInspeccion == null) validarInspeccion = true;

    const taskId = this.pendingTasks.addTask(`Sincronizando orden de trabajo ${ordenTrabajo.NumInforme}`)
    try {

    // Buscamos la orden de trabajo en base de datos local
    if (showMessages) await this.loader.show('Subiendo orden de trabajo...')

    // Parcheamos el valor
    inspeccionFormGroup.patchValue(ordenTrabajo)

    // Validamos el formulario
    // TODO: Validar los campos generales de la inspección.

    // Iteramos por los GruposComprobacion y solo validamos los visibles
    if (validarInspeccion) {
      const oca29 = (inspeccionFormGroup.value as InspeccionClass).ListaGrupos.map(p => p.ListaParametros.find(q => q.Codigo == 'OCA.29')).filter(p => !!p)[0]
      const oca30 = (inspeccionFormGroup.value as InspeccionClass).ListaGrupos.map(p => p.ListaParametros.find(q => q.Codigo == 'OCA.30')).filter(p => !!p)[0]
      for (let grupo of (inspeccionFormGroup.get('ListaGrupos') as FormArray).controls) {
        let formGroup = grupo as FormGroup
        let visible = this.isGroupVisible(formGroup, inspeccionFormGroup.get('ListaGrupos') as FormArray)
        
        // TODO: Comprobar grupos de comprobación dependientes
        if (visible && (!formGroup.valid)) {
          console.log(`El grupo de comprobación ${formGroup.get('GrupoComprobacion')?.value} contiene errores.`)
          if (ordenTrabajo.SePuedeSaltarLogica) {
            if (oca29 != null && (oca29.Valor == null || oca29.Valor == '')) throw new Error(`No se puede subir la inspección porque el campo ${oca29.Parametro} no está relleno.`)
            if (oca30 != null && (oca30.Valor == null || oca30.Valor == '')) throw new Error(`No se puede subir la inspección porque el campo ${oca30.Parametro} no está relleno.`)
            if (showMessages) await this.loader.hide()
            if (await this.alertService.showConfirm(`La inspección contiene errores o alguno de los campos no está relleno. ¿Quieres continuar?`, 'Errores en los campos', 'Sí, continuar', 'No, cancelar')) {
              if (showMessages) await this.loader.show('Subiendo orden de trabajo...')
              break
            }
            if (showMessages) await this.loader.show('Subiendo orden de trabajo...')
          }
          throw new Error('No se puede subir la inspección porque contiene errores. Revisa los campos y vuelve a intentarlo.')  
        }
      }
    }    
    // Subimos la inspección
    await this.service.Inspecciones.uploadInspeccion(ordenTrabajo)

    // Obtenemos las imágenes de la inspección (en base de datos)
    let imagenes = await this.service.Inspecciones.getImagenes(ordenTrabajo.Id)

    // Obtenemos las imágenes locales de la inspección
    let imagenesLocales = await this.database.Fotos.getByOrdenLocalId(ordenTrabajo.LocalId)

    // Eliminamos las imágenes que ya no estén en local
    let imagenesBorrar = imagenes.filter(p => imagenesLocales.find(q => q.Id == p.Id) == null)
    imagenesBorrar = imagenesBorrar.filter(p => p.SubidaDesdeMovil == null || p.SubidaDesdeMovil == false)
    if (imagenesBorrar.length) {
      for (let imagenBorrar of imagenesBorrar) {
        if (showMessages) await this.loader.hide();
        if (showMessages) if (showMessages) await this.loader.show(`Eliminando imagen ${imagenBorrar.FileName}`);
        await this.service.Inspecciones.deleteImagen(ordenTrabajo.Id, imagenBorrar.Id)
      }
    }

    // Iteramos por las imágenes y subimos las que sean nuevas o no cuadre el SHA256
    for (let imagen of imagenesLocales) {
      let imagenBD: ImagenInspeccionClass = null
      if (imagen.Id != 0) {
        imagenBD = imagenes.find(p => p.Id == imagen.Id)
      }
      if (imagenBD != null && imagenBD.SHA256 != imagen.SHA256) {
        // Eliminamos la imagen para subir la nueva.
        if (showMessages) await this.loader.hide();
        if (showMessages) await this.loader.show(`Actualizando imagen ${imagen.FileName}`);

        await this.service.Inspecciones.deleteImagen(ordenTrabajo.Id, imagenBD.Id)
        const blob = await (await fetch(imagen.FotoData)).blob()
        const file = new File([blob], imagen.FileName)
        await this.service.Inspecciones.postImagen({
          Id: imagen.Id,
          FileName: imagen.FileName,
          InspeccionId: ordenTrabajo.Id,
          SHA256: imagen.SHA256,
          Categoria: imagen.Categoria
        }, file)

      } else if (imagen.Id == 0) {
        if (showMessages) await this.loader.hide();
        if (showMessages) await this.loader.show(`Subiendo imagen ${imagen.FileName}`);

        const blob = await (await fetch(imagen.FotoData)).blob()
        const file = new File([blob], imagen.FileName)
        let newId = await this.service.Inspecciones.postImagen({
          Id: imagen.Id,
          FileName: imagen.FileName,
          InspeccionId: ordenTrabajo.Id,
          SHA256: imagen.SHA256,
          Categoria: imagen.Categoria
        }, file)

        if (imagen.Id != newId) {
          imagen.Id = newId
          await this.database.Fotos.update(imagen)
        }

      }
    }
    await this.service.Inspecciones.changesubidadesdemovilimagenes(ordenTrabajo.Id)

    // Obtenemos los adjuntos de la inspección (en base de datos)
    let adjuntos = await this.service.Inspecciones.getFicheros(ordenTrabajo.Id)

    // Obtenemos los adjuntos locales de la inspección
    let adjuntosLocales = await this.database.Adjuntos.getByOrdenLocalId(ordenTrabajo.LocalId)

    // Eliminamos los adjuntos que ya no estén en local
    for (let adjuntoBorrar of adjuntosLocales.filter(p => p.Borrado === true)) {
      if (adjuntoBorrar.Id !== 0) {
        await this.loader.hide()
        await this.loader.show(`Eliminando archivo ${adjuntoBorrar.FileName}`)
        await this.service.Inspecciones.deleteFichero(ordenTrabajo.Id, adjuntoBorrar.Id)
        await this.database.Adjuntos.delete(adjuntoBorrar)
      }
    }

    // Iteramos por los adjuntos y subimos los que sean nuevos o no cuadre el SHA256
    for (let adjunto of adjuntosLocales) {
      let adjuntoBD: AdjuntoInspeccionClass = null
      if (adjunto.Id != 0) {
        adjuntoBD = adjuntos.find(p => p.Id == adjunto.Id)
      }
      if (adjuntoBD != null && adjuntoBD.SHA256 != adjunto.SHA256) {
        // Eliminamos el adjunto para subir el nuevo.
        if (showMessages) await this.loader.hide();
        if (showMessages) await this.loader.show(`Actualizando fichero ${adjunto.FileName}`);

        await this.service.Inspecciones.deleteFichero(ordenTrabajo.Id, adjuntoBD.Id)
        const blob = await (await fetch(adjunto.AdjuntoData)).blob()
        const file = new File([blob], adjunto.FileName)
        await this.service.Inspecciones.postFichero({
          Id: adjunto.Id,
          FileName: adjunto.FileName,
          InspeccionId: ordenTrabajo.Id,
          SHA256: adjunto.SHA256,
          Categoria: adjunto.Categoria
        }, file)

      } else if (adjunto.Id == 0) {
        if (showMessages) await this.loader.hide();
        if (showMessages) await this.loader.show(`Subiendo fichero ${adjunto.FileName}`);
        
        const blob = await (await fetch(adjunto.AdjuntoData)).blob()
        const file = new File([blob], adjunto.FileName)
        let newId = await this.service.Inspecciones.postFichero({
          Id: adjunto.Id,
          FileName: adjunto.FileName,
          InspeccionId: ordenTrabajo.Id,
          SHA256: adjunto.SHA256,
          Categoria: adjunto.Categoria
        }, file)
        if (adjunto.Id != newId) {
          adjunto.Id = newId
          await this.database.Adjuntos.update(adjunto)
        }
      }
    }

    if (showMessages) await this.loader.hideAll();

    } finally {
      this.pendingTasks.removeTask(taskId)
    }

  }

}
